import { CalendarClock, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ConsultationForm from "@/components/ConsultationForm";

const VALUE_POINTS = [
  "15 focused minutes with a senior practitioner, not a sales rep.",
  "We listen to your project, pressure-test the scope, and outline a path forward.",
  "If we're the wrong fit, we'll tell you and recommend who is.",
];

const Consultation = () => {
  return (
    <main data-testid="page-consultation">
      <PageHero
        eyebrow="Free Consultation"
        title="Book a 15-minute consultation."
        intro="Tell us a date, time, and how you'd like to meet. We'll confirm by email within one business day."
      />

      <section className="bg-[#F8F9FA] border-b border-gray-200" data-testid="consultation-value">
        <div className="container-kc py-10 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALUE_POINTS.map((v, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div className="flex items-start gap-4">
                  <CheckCircle2 size={20} className="text-[#C5A880] mt-1 shrink-0" />
                  <p className="text-[14px] text-gray-700 leading-relaxed">{v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-kc">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <aside className="lg:col-span-4">
              <Reveal>
                <div className="flex items-center gap-3 mb-6">
                  <span className="hairline--gold" />
                  <span className="eyebrow">How It Works</span>
                </div>
                <h2 className="h-display text-3xl md:text-4xl leading-tight">
                  Simple, no obligation.
                </h2>
                <ol className="mt-10 space-y-8">
                  <li className="flex items-start gap-5">
                    <span className="kc-index w-10 shrink-0">01</span>
                    <div>
                      <h3 className="font-serif text-lg text-[#000000] leading-snug">Pick a slot</h3>
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                        Choose your preferred date, time, and meeting format (Zoom, phone, or in-person at our DC office).
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-5">
                    <span className="kc-index w-10 shrink-0">02</span>
                    <div>
                      <h3 className="font-serif text-lg text-[#000000] leading-snug">We confirm</h3>
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                        A calendar invite with the meeting link or call-in details lands in your inbox within one business day.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-5">
                    <span className="kc-index w-10 shrink-0">03</span>
                    <div>
                      <h3 className="font-serif text-lg text-[#000000] leading-snug">We talk</h3>
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                        Fifteen focused minutes. You leave with a clear sense of what a path forward looks like.
                      </p>
                    </div>
                  </li>
                </ol>

                <div className="mt-12 p-6 bg-[#F8F9FA] border-l-2 border-[#C5A880] flex items-start gap-4">
                  <CalendarClock size={22} className="text-[#C5A880] shrink-0 mt-1" />
                  <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#000000]">No obligation</div>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                      This call is free. There&apos;s no contract, no pitch deck, no follow-up sales sequence.
                    </p>
                  </div>
                </div>
              </Reveal>
            </aside>

            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <ConsultationForm />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Consultation;
